import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import {
  Folder,
  Key,
  Delete,
  ArrowBack,
  Storage,
  DataObject,
  DeleteForever
} from '@mui/icons-material';
import { useTheme, styled } from '@mui/material/styles';
import { memoryAPI } from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const JsonContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  maxHeight: '500px',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  border: theme.palette.mode === 'dark' 
    ? 'none' 
    : `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' 
    ? 'var(--agentflow-shadow-elevation)'
    : 'none',
}));

const AdminMemoryBrowserPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { group, key } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page')) || 1;
  
  const [groups, setGroups] = useState([]);
  const [keys, setKeys] = useState([]);
  const [keysPagination, setKeysPagination] = useState(null);
  const [memoryData, setMemoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, target: null });

  // Determine current view based on URL parameters
  const view = key ? 'data' : group ? 'keys' : 'groups';
  const currentGroup = group;
  const currentKey = key;

  // Load data based on URL parameters
  useEffect(() => {
    if (view === 'groups') {
      loadGroups();
    } else if (view === 'keys' && currentGroup) {
      loadKeys(currentGroup, currentPage);
    } else if (view === 'data' && currentGroup && currentKey) {
      loadData(currentGroup, currentKey);
    }
  }, [view, currentGroup, currentKey, currentPage]);

  const loadGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await memoryAPI.getGroups();
      setGroups(response.groups);
    } catch (err) {
      setError('Failed to load memory groups: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadKeys = async (group, page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await memoryAPI.getKeys(group, page, 20);
      setKeys(response.keys);
      setKeysPagination(response.pagination);
    } catch (err) {
      setError('Failed to load keys: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (group, key) => {
    setLoading(true);
    setError(null);
    try {
      const response = await memoryAPI.getData(group, key);
      setMemoryData(response);
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (group) => {
    navigate(`/admin/memory/${encodeURIComponent(group.group)}`);
  };

  const handleKeyClick = (key) => {
    navigate(`/admin/memory/${encodeURIComponent(currentGroup)}/${encodeURIComponent(key.key)}`);
  };

  const handleBreadcrumbClick = (targetView) => {
    if (targetView === 'groups') {
      navigate('/admin/memory');
    } else if (targetView === 'keys') {
      navigate(`/admin/memory/${encodeURIComponent(currentGroup)}`);
    }
  };

  const handlePageChange = (event, newPage) => {
    setSearchParams({ page: newPage.toString() });
  };

  const handleDelete = async () => {
    const { type, target } = deleteDialog;
    setLoading(true);
    try {
      if (type === 'group') {
        await memoryAPI.deleteGroup(target);
        setDeleteDialog({ open: false, type: null, target: null });
        // If we were viewing this group, go back to groups view
        if (currentGroup === target) {
          navigate('/admin/memory');
        } else {
          // Reload groups if we're on the groups page
          if (view === 'groups') {
            loadGroups();
          }
        }
      } else if (type === 'key') {
        await memoryAPI.deleteKey(currentGroup, target);
        setDeleteDialog({ open: false, type: null, target: null });
        // If we were viewing this key, go back to keys view
        if (currentKey === target) {
          navigate(`/admin/memory/${encodeURIComponent(currentGroup)}`);
        } else {
          // Reload keys if we're on the keys page
          if (view === 'keys') {
            loadKeys(currentGroup, currentPage);
          }
        }
      }
    } catch (err) {
      setError('Failed to delete: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="memory navigation" sx={{ mb: 2 }}>
      <Link 
        color="inherit" 
        onClick={() => handleBreadcrumbClick('groups')}
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        <Storage sx={{ mr: 0.5 }} fontSize="inherit" />
        Memory Groups
      </Link>
      {currentGroup && (
        <Link 
          color="inherit" 
          onClick={() => handleBreadcrumbClick('keys')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <Folder sx={{ mr: 0.5 }} fontSize="inherit" />
          {currentGroup}
        </Link>
      )}
      {currentKey && (
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <Key sx={{ mr: 0.5 }} fontSize="inherit" />
          {currentKey}
        </Typography>
      )}
    </Breadcrumbs>
  );

  const renderGroups = () => (
    <Grid container spacing={3}>
      {groups.map((group) => (
        <Grid item xs={12} sm={6} md={4} key={group.group}>
          <StyledCard onClick={() => handleGroupClick(group)}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <Folder sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" component="div">
                    {group.group}
                  </Typography>
                </Box>
                <Tooltip title="Delete Group">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteDialog({ open: true, type: 'group', target: group.group });
                    }}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              </Box>
              <Chip 
                label={`${group.key_count} keys`} 
                size="small" 
                sx={{ mt: 1 }}
                color="primary"
                variant="outlined"
              />
            </CardContent>
          </StyledCard>
        </Grid>
      ))}
    </Grid>
  );

  const renderKeys = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Keys in "{currentGroup}"
      </Typography>
      <List>
        {keys.map((key) => (
          <ListItem
            key={key.key}
            button
            onClick={() => handleKeyClick(key)}
            divider
          >
            <Key sx={{ mr: 2, color: theme.palette.secondary.main }} />
            <ListItemText
              primary={key.key}
              secondary={key.modified_at ? `Modified: ${new Date(key.modified_at).toLocaleString()}` : 'No modification date'}
            />
            <ListItemSecondaryAction>
              <Tooltip title="Delete Key">
                <IconButton
                  edge="end"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteDialog({ open: true, type: 'key', target: key.key });
                  }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      
      {keysPagination && keysPagination.pages > 1 && (
        <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
          <Pagination
            count={keysPagination.pages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );

  const renderData = () => (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">
          Data for "{currentGroup}" / "{currentKey}"
        </Typography>
        <Tooltip title="Delete This Data">
          <IconButton
            color="error"
            onClick={() => setDeleteDialog({ open: true, type: 'key', target: currentKey })}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
      
      {memoryData && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <DataObject sx={{ mr: 1 }} />
            JSON Data
          </Typography>
          <JsonContainer>
            {JSON.stringify(memoryData.data, null, 2)}
          </JsonContainer>
          
          {memoryData.modified_at && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Last Modified: {new Date(memoryData.modified_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <Storage sx={{ mr: 1 }} />
        Global Memory Browser
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Browse and manage persistent memory data cached by agents across tickets and runs.
      </Typography>

      {renderBreadcrumbs()}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" sx={{ my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {view === 'groups' && renderGroups()}
          {view === 'keys' && renderKeys()}
          {view === 'data' && renderData()}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, target: null })}
      >
        <DialogTitle>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteDialog.type === 'group' ? 'the entire group' : 'this key'}{' '}
            "{deleteDialog.target}"?
            {deleteDialog.type === 'group' && ' This will delete all keys and data in this group.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, type: null, target: null })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMemoryBrowserPage;